import Backdrop from '@mui/material/Backdrop'
import MUIDrawer, { DrawerProps } from '@mui/material/Drawer'
import React, { useState } from 'react'

import { CloseIcon } from '@/atoms/SvgIcon/SvgIcon'
import { useZIndexContext, ZIndexContext } from '@/common/z-index'
import { E2E } from '@/lib/e2e-utils'
import { useChatPosition } from '@/refactor/hooks/useChatPosition'
import styled, { css, mediaQuery, muiUtils } from '@/styles'

import { IconButton } from '../Button/Button'

export interface IProps extends Omit<DrawerProps, 'id' | 'classes' | 'variant'>, E2E {
	open?: boolean
	width?: number
	zIndex?: number
	onClose?(e: {}): void
	closeDisabled?: boolean
}

const CloseIconButton = styled(IconButton)`
	position: absolute;
	z-index: 1;
	top: ${(p) => p.theme.spacing(4)};
	right: ${(p) => p.theme.spacing(3)};

	width: 24px;

	${muiUtils.desktop} {
		top: ${(p) => p.theme.spacing(6)};
		right: ${(p) => p.theme.spacing(8)};
	}
`

const StyledMUIDrawer = styled(MUIDrawer)<{ width: number; zIndex?: number }>`
	${({ width: $width, zIndex: $zIndex }) => css`
		& .MuiPaper-root.MuiDrawer-paper {
			overflow: hidden;
			${mediaQuery.above('mobile')`width: ${$width}px;`}
			${mediaQuery.mobileOnly`width: 100vw;`}

		${mediaQuery.smallMobile`
			top: initial;
		`}
		}

		& + & {
			.MuiBackdrop-root {
				background: none;
			}
		}

		${$zIndex !== undefined
			? `
			z-index: ${$zIndex} !important;
		& .MuiPaper-root.MuiDrawer-paper {
			z-index: ${$zIndex + 2} !important;
		}

		> .MuiBackdrop-root {
			z-index: ${$zIndex + 1} !important;
		}
	`
			: ''}
	`}
`

const StyledBackdrop = styled(Backdrop)`
	&.MuiBackdrop-root {
		z-index: 10000;
	}
	& ~ .MuiPaper-root {
		z-index: 10001;
	}
`

export function Drawer(props: IProps) {
	const {
		className,
		children,
		open = true,
		anchor = 'right',
		onClose,
		width = 600,
		closeDisabled,
		e2e = 'Drawer.Container',
		container = document.getElementById('root'),
		PaperProps,
		zIndex,
		...rest
	} = props

	const [containerRef, setRef] = useState<HTMLDivElement | null>(null)

	useChatPosition({
		containerRef: containerRef,
		trigger: open,
	})

	const zIndexToApply = useZIndexContext({ overrideValue: zIndex, defaultValue: 10000 })

	return (
		<StyledMUIDrawer
			ModalProps={{ container }}
			className={className}
			width={width}
			open={open}
			anchor={anchor}
			onClose={onClose}
			variant="temporary"
			disableRestoreFocus
			disableEnforceFocus
			BackdropComponent={StyledBackdrop}
			PaperProps={{
				...PaperProps,
				ref: setRef,
				'data-test': e2e,
			}}
			zIndex={zIndexToApply}
			{...rest}
		>
			{!!onClose && (
				<CloseIconButton data-test="Drawer.Button.Close" onClick={(e) => onClose?.(e)} disabled={closeDisabled}>
					<CloseIcon size={24} />
				</CloseIconButton>
			)}
			<ZIndexContext.Provider value={zIndexToApply}>{children}</ZIndexContext.Provider>
		</StyledMUIDrawer>
	)
}

export default Drawer
